'use client';

import React from 'react';
import styled from 'styled-components';
import { Modal } from '@/components/modals/Modal';
import Dropdown from '@/components/forms/controls/Dropdown';
import Button from '@/components/ui-components/Button';
import { CloseIconModal, RefreshIcon } from '@/components/icons/ModalIcons';

interface NotificationDelayModalProps {
  isOpen: boolean;
  onClose: () => void;
  notificationType: string;
  monitorName?: string;
  repeatValue: string;
  delayValue: string;
  onRepeatChange: (value: string) => void;
  onDelayChange: (value: string) => void;
  onSave: () => void;
}

const ModalContent = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #FB6F2A 0%, #D33324 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  
  svg {
    width: 32px;
    height: 32px;
    color: #ffffff;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  text-align: center;
  margin: 0 0 32px 0;
`;

const InfoSection = styled.div`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;
  
  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

export const NotificationDelayModal: React.FC<NotificationDelayModalProps> = ({
  isOpen,
  onClose,
  notificationType,
  monitorName,
  repeatValue,
  delayValue,
  onRepeatChange,
  onDelayChange,
  onSave,
}) => {
  const repeatOptions = [
    { label: "Don't repeat", value: 'none' },
    { label: 'Every 5 minutes', value: '5m' },
    { label: 'Every 15 minutes', value: '15m' },
    { label: 'Every 30 minutes', value: '30m' },
    { label: 'Every hour', value: '1h' },
    { label: 'Every 6 hours', value: '6h' },
    { label: 'Every 12 hours', value: '12h' },
  ];

  const delayOptions = [
    { label: "Don't delay", value: 'none' },
    { label: '5 minutes', value: '5m' },
    { label: '15 minutes', value: '15m' },
    { label: '30 minutes', value: '30m' },
    { label: '1 hour', value: '1h' },
    { label: '2 hours', value: '2h' },
    { label: '6 hours', value: '6h' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="600px">
      <ModalContent>
        <CloseButton onClick={onClose}>
          <CloseIconModal />
        </CloseButton>

        <IconWrapper>
          <RefreshIcon />
        </IconWrapper>

        <Title>Notification Repeat and Delay</Title>

        <InfoSection>
          <InfoLabel>Currently editing settings for:</InfoLabel>
          <InfoValue>{notificationType}</InfoValue>
          {monitorName && (
            <>
              <InfoLabel style={{ marginTop: '12px' }}>Monitor:</InfoLabel>
              <InfoValue>&lt;{monitorName}&gt;</InfoValue>
            </>
          )}
        </InfoSection>

        <FormGrid>
          <FormField>
            <Label>Repeat notification</Label>
            <Dropdown
              options={repeatOptions}
              value={repeatValue}
              onChange={onRepeatChange}
              placeholder="Select repeat"
            />
          </FormField>

          <FormField>
            <Label>Delay notification</Label>
            <Dropdown
              options={delayOptions}
              value={delayValue}
              onChange={onDelayChange}
              placeholder="Select delay"
            />
          </FormField>
        </FormGrid>

        <ButtonGroup>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave}>
            Save changes
          </Button>
        </ButtonGroup>
      </ModalContent>
    </Modal>
  );
};

export default NotificationDelayModal;

